import { useCallback, useRef, useState } from 'react';
import { useEidEasySign } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/hooks/useEidEasySign';
import { useInvestor } from '@dealstack/frontend/investor/pages/Investment/hooks/useInvestor';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams';
import { getPrimaryAddressCountry } from '@dealstack/frontend/investor/pages/Investment/common/DocumentWorkflowContracts/utils';
import { webIsProduction } from '@dealstack/frontend/shared/environment';
import {
	canSignKycForm,
	getKycFormAllowedSigningMethods,
} from '@dealstack/frontend/investor/pages/Investment/common/InvestorKycFormsTable/KycFormSignAction.utils';
import { useDownloadFileUsingFileStorageUrl } from '@dealstack/frontend/shared/modules/document/file/hooks/useDownloadFileUsingFileStorageUrl';
import { formatUserFullName } from '@dealstack/shared/lib/format/user';
import { getValidFileNameForDownload } from '@dealstack/shared/lib/common/file/file';
import { FileExtension } from '@dealstack/shared/lib/common/dataExchange/common';
import type { EidEasyCompleteRes } from '@dealstack/shared/lib/generated';
import type { SignatureSuccessDetails } from '@dealstack/frontend/shared/components/EidEasySigningModal/steps/SignatureSuccessConfirmation/SignatureSuccessConfirmation.types';
import type { FormPair } from '@dealstack/frontend/investor/pages/Investment/common/InvestorKycFormsTable/types';
import { usePreFillKycForm } from '@dealstack/frontend/investor/pages/Investment/common/KycFormPreFill/usePreFillKycForm';
import type { KycFormSigning, UseKycFormSigningOptions } from './useKycFormSigning.types';

/**
 * Owns the KYC form eID Easy signing flow at page level rather than in the row: the post-signing
 * refetch re-derives the table's columns, and a cell-mounted modal would die mid success-screen.
 * Country/method/OTP derivation matches the contract flow's, kept here once for both call sites.
 */
export const useKycFormSigning = ({
	onSigned,
	kycWorkflowId,
}: UseKycFormSigningOptions): KycFormSigning => {
	const { dealId, mipId } = useRequiredParams('dealId', 'mipId');
	const [signingForm, setSigningForm] = useState<FormPair | null>(null);
	// Carries the signing method, timestamp and session the success screen reports back.
	const [completionResult, setCompletionResult] = useState<EidEasyCompleteRes | null>(null);
	const [isDownloadingSignedPdf, setIsDownloadingSignedPdf] = useState(false);
	// Mantine keeps children mounted through the ~200ms exit transition, so clearing the flow's
	// state on `onClose` is visible as a flip mid-fade. Close first, tear down after.
	const [isClosing, setIsClosing] = useState(false);

	// Cached under useInvestor's fixed query key — same data and derivation as the contract flow,
	// so the modal gets the same recommended methods and pre-filled OTP contact info.
	const { data: investorData } = useInvestor();
	const countryCode = getPrimaryAddressCountry(investorData?.person);
	const { downloadFile } = useDownloadFileUsingFileStorageUrl();
	const preFill = usePreFillKycForm(kycWorkflowId);
	// A second click while the copy is being filled would fill it twice.
	const isPreparingRef = useRef(false);

	const {
		startSign,
		selectMethod,
		goBack,
		confirmReview,
		confirmSmartId,
		confirmFreja,
		confirmOtp,
		submitOtpCode,
		resendOtpCode,
		otpVerifying,
		otpCodeError,
		otpChannel,
		otpMaskedContact,
		confirmSignature,
		onSignatureConfirmed,
		reset,
		status,
		challengeCode,
		error,
		selectedMethod,
		isSigningInProgress,
		isModalOpen,
	} = useEidEasySign({
		// No `documentWorkflowInstanceId`: `startSign` takes the row's id explicitly, and an option
		// that changed per form would re-derive it — and the table's columns — on every click.
		dealId,
		programmeId: mipId,
		onComplete: (result) => {
			setCompletionResult(result ?? null);
			onSigned();
		},
	});

	const startSigningForm = useCallback(
		async (form: FormPair) => {
			// Defensive: the row only offers Sign when this holds, and the backend's prepare
			// endpoint refuses anything else with a 409.
			if (!canSignKycForm(form) || !form.signatureId || isPreparingRef.current) {
				return;
			}
			// Filled first: prepare signs whatever file the instance holds, so signing the
			// blank would put the investor's signature on an empty form.
			isPreparingRef.current = true;
			const preFilled = await preFill(form).finally(() => {
				isPreparingRef.current = false;
			});
			const signatureId = preFilled.signatureId ?? form.signatureId;
			// Re-arm: a close whose exit transition never reported back must not wedge the modal
			// shut, and the abandoned ceremony's captured context would otherwise route this one.
			if (isClosing) {
				reset();
			}
			setIsClosing(false);
			setCompletionResult(null);
			setSigningForm(preFilled);
			startSign(signatureId, preFilled.instanceId, preFilled.fileId ?? undefined);
		},
		[startSign, isClosing, reset, preFill]
	);

	const closeSigningModal = useCallback(() => {
		setIsClosing(true);
	}, []);

	const finishClosingSigningModal = useCallback(() => {
		reset();
		setSigningForm(null);
		setCompletionResult(null);
		setIsClosing(false);
	}, [reset]);

	const signedFileId = completionResult?.fileId ?? signingForm?.fileId ?? undefined;

	const handleDownloadSignedPdf = useCallback(async () => {
		if (!signedFileId) {
			return;
		}
		setIsDownloadingSignedPdf(true);
		try {
			await downloadFile({
				fileId: signedFileId,
				dealId,
				fileName: getValidFileNameForDownload(
					signingForm?.fileName ?? 'document',
					FileExtension.pdf
				),
			});
		} finally {
			setIsDownloadingSignedPdf(false);
		}
	}, [signedFileId, downloadFile, dealId, signingForm?.fileName]);

	const successDetails: SignatureSuccessDetails | undefined = signingForm
		? {
				signerName:
					formatUserFullName({
						firstName: investorData?.user.firstName,
						lastName: investorData?.user.lastName,
					}) || null,
				signingMethod: completionResult?.signingMethod ?? null,
				signedAt: completionResult?.signedAt ?? null,
				session: completionResult?.session,
				isDownloading: isDownloadingSignedPdf,
				onDownloadSignedPdf: signedFileId ? handleDownloadSignedPdf : undefined,
			}
		: undefined;

	return {
		startSigningForm,
		signingModalProps: {
			status,
			challengeCode,
			error,
			selectedMethod,
			isSigningInProgress,
			isModalOpen: isModalOpen && !isClosing,
			// Once signing completes, preview the freshly signed document
			fileId: signedFileId,
			fileName: signingForm?.fileName ?? undefined,
			successDetails,
			allowedMethods: getKycFormAllowedSigningMethods(webIsProduction),
			restrictToCountry: true,
			countryCode,
			defaultPhone: investorData?.user.phoneNumber ?? undefined,
			defaultPhonePrefix: investorData?.user.telephonePrefix ?? undefined,
			defaultEmail: investorData?.user.email ?? undefined,
			onClose: closeSigningModal,
			onExitTransitionEnd: finishClosingSigningModal,
			onConfirmReview: confirmReview,
			onSelectMethod: selectMethod,
			onGoBack: goBack,
			onConfirmSmartId: (idcode, smartIdCountryCode) => {
				void confirmSmartId(idcode, smartIdCountryCode);
			},
			onConfirmSignature: (signatureDataUrl, source) => {
				void confirmSignature(signatureDataUrl, source);
			},
			onSignatureConfirmed: (signatureBase64) => {
				void onSignatureConfirmed(signatureBase64);
			},
			onConfirmFreja: confirmFreja,
			onConfirmOtp: confirmOtp,
			onSubmitOtpCode: (code) => {
				void submitOtpCode(code);
			},
			onResendOtpCode: resendOtpCode,
			otpVerifying,
			otpCodeError,
			otpChannel,
			otpMaskedContact,
		},
	};
};
